import { updateUserEmailApi } from "@/api/services/main/auth";
import { Button } from "@/components/elements/Button";
import { LoadingScreen } from "@/components/elements/Loading/Loading";
import { LoginLogo } from "@/features/auth/components/LoginLogo";
import { useCacheLocal } from "@/hooks/useCacheLocal";
import { AppRoutes } from "@/routes/config";
import { background, divider, text } from "@/theme/colors";
import { elevation, rounded } from "@/theme/variables";
import { showError } from "@/utils/error";
import { Box, Stack, Typography } from "@mui/material";
import { FC, useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { CENTER_TOKEN_KEY, EMAIL_KEY, EXTERNAL_ID_KEY } from "@/constants/common";
import { LOGIN_CACHED_EMAIL } from "@/features/auth/constant/login";
import { createUrlWithParams } from "@/utils/url";
import Cookies from "js-cookie";

type TConfirmEmailPageProps = {};

export const ConfirmEmailPage: FC<TConfirmEmailPageProps> = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const cachedEmailStore = useCacheLocal(LOGIN_CACHED_EMAIL);
  const isAlreadyCache = cachedEmailStore.isExist();
  const [result, setResult] = useState<"failed" | "success">();
  const [searchParams] = useSearchParams();
  const tokenParam = searchParams.get("token");
  const emailParam = searchParams.get(EMAIL_KEY);
  const externalIdParam = searchParams.get(EXTERNAL_ID_KEY);

  const cachedData = useMemo(() => {
    if (!isAlreadyCache) return {};
    const cachedEmailMap = cachedEmailStore.getJSON();
    if (typeof cachedEmailMap === "object") return cachedEmailMap as Record<string, string>;
    return {};
  }, [cachedEmailStore, isAlreadyCache]);

  const hasRequiredParams = Boolean(emailParam && tokenParam);

  useEffect(() => {
    if (!externalIdParam) return;
    if (externalIdParam in cachedData) {
      cachedEmailStore.setJSON({ ...cachedData, [externalIdParam]: emailParam });
    }
  }, [externalIdParam, emailParam]);

  useEffect(() => {
    if (!hasRequiredParams) return;
    setIsLoading(true);
    handleUpdateUserEmail().finally(() => setIsLoading(false));
  }, []);

  const handleUpdateUserEmail = async () => {
    try {
      const data = { email: emailParam ?? "", token: tokenParam ?? "" };
      await updateUserEmailApi(data);
      setResult("success");
    } catch (error) {
      showError(error);
      setResult("failed");
    }
  };

  const isAuthenticated = Boolean(Cookies.get(CENTER_TOKEN_KEY));
  if (!hasRequiredParams) return <Navigate to={AppRoutes.pageNotFound} replace />;
  if (isLoading) return <LoadingScreen />;

  const isSuccess = result === "success";

  return (
    <Stack gap="32px" alignItems="center">
      <LoginLogo />
      <Stack
        gap="24px"
        width="464px"
        maxWidth="100%"
        p="24px"
        sx={{ background: background.white, borderRadius: rounded.md, boxShadow: elevation.lv1 }}
      >
        <Typography variant="head20Bold" color={text.primary} textAlign="center">
          {isSuccess ? "メールアドレスの変更完了" : "メールアドレスの変更に失敗しました"}
        </Typography>

        {isSuccess ? (
          <Typography variant="body14" color={text.secondary} sx={{ whiteSpace: "pre-line" }}>
            {"メールアドレスの変更完了いたしました。\nGoogleログインをご利用の方は、再設定をお願いします。\nパスキーに旧メールアドレスが表示される場合は、パスキーを削除して再登録してください。"}
          </Typography>
        ) : (
          <Typography variant="body14" color={text.secondary} sx={{ whiteSpace: "pre-line" }}>
            {"認証URLの有効期限が切れているか、無効なリンクです。\nお手数ですが、メールアドレスの変更をやり直してください。"}
          </Typography>
        )}

        {isSuccess && (
          <Stack gap="8px" pb="16px" borderBottom={`1px solid ${divider.middle}`}>
            <Typography variant="cap12" color={text.secondary}>
              変更後のメールアドレス
            </Typography>
            <Typography variant="body14Semi" color={text.primary}>
              {emailParam}
            </Typography>
          </Stack>
        )}

        <Box>
          {isAuthenticated && (
            <Button variant="primary" fullWidth onClick={() => navigate(AppRoutes.userSettingDetail)}>
              ユーザー設定ページへ
            </Button>
          )}
          {!isAuthenticated && (
            <Button
              type="submit"
              variant="primary"
              fullWidth
              onClick={() =>
                navigate(
                  createUrlWithParams(AppRoutes.loginCredentials, {
                    externalId: externalIdParam,
                    email: emailParam,
                    redirectUrl: AppRoutes.userSettingDetail,
                  }),
                )
              }
            >
              <Typography variant="body14Semi">ログイン画面へ</Typography>
            </Button>
          )}
        </Box>
      </Stack>
    </Stack>
  );
};
